import ArticleTile from 'app/components/partials/article-tile';
import { parseVideoUrl } from 'app/utilities/video';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

/*
    The player is held back behind a still of the video until the reader asks for
    it, and started by a button of ours beside the heading.

    That is the only way to be rid of YouTube's own play button and the title and
    watermark it sits under: the parameters that used to hide them are no longer
    honoured, so the player has to be kept off the page rather than dressed down.
    Nothing of YouTube's is loaded until the button is pressed, which is a page
    that is quicker to open as well as a tidier one.
*/
/*
    The full size still is not kept for every video, and the smaller one always
    is, so a video without the first falls back to the second rather than to an
    empty frame. Once, or a still that is missing both would ask for the second
    over and over.
*/
const handlePosterError = (id) => (event) => {
    const image = event.currentTarget;

    if (image.dataset.fallback) {
        return;
    }

    image.dataset.fallback = 'true';
    image.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
};

const ShortVideo = ({ id, title }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    // Started by the same press that puts the player on the page, the gesture
    // for it having been made here rather than inside the frame
    const embedUrl = `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&iv_load_policy=3&playsinline=1`;

    return (
        <div className="news-short-video-panel is-boxed">
            <div className="news-short-video-header">
                {title && <h2 className="news-short-video-title">{title}</h2>}
                {!isPlaying &&
                    <button
                        type="button"
                        className="news-short-video-play"
                        aria-label={title ? `Play ${title}` : 'Play video'}
                        onClick={() => setIsPlaying(true)} />
                }
            </div>
            <div className="news-short-video-frame">
                {isPlaying ?
                    <iframe
                        title={title || 'Video'}
                        className="video"
                        src={embedUrl}
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen={true}
                    /> :
                    /*
                        The still is a second way to start the video, because a
                        frame standing where a player will be is something a
                        reader presses whether or not it is where the control is.

                        The button beside the heading is the one control of the
                        two: this is hidden from assistive software and left out
                        of the tab order rather than announced again, so a reader
                        who does not point at it is not offered the same thing
                        twice over.
                    */
                    <button
                        type="button"
                        className="news-short-video-trigger"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={() => setIsPlaying(true)}>
                        {/*
                            Straight from YouTube, so a still costs the CMS
                            nothing to keep alongside the URL. It is the video
                            itself rather than an illustration of it, and
                            decorative next to the heading that names it, so it
                            is left without a description.
                        */}
                        <img
                            className="news-short-video-poster"
                            src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onError={handlePosterError(id)}
                        />
                    </button>
                }
            </div>
        </div>
    );
};

ShortVideo.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string
};

/*
    A story beside a short video: the story as the same card the news landing
    page shows under Latest, across two thirds of the row, and the video in the
    third it leaves.

    `tile-grid` is what splits the row into columns, shared with the landing page
    hero, and it is also what the card takes its styling from, so the card here
    is the one there rather than a copy of it.
*/
const NewsShortVideo = ({ data }) => {
    const {
        news,
        news_title: newsTitle,
        video_title: videoTitle,
        video_url: videoUrl
    } = data;

    const video = parseVideoUrl(videoUrl);
    const videoId = video && video.provider === 'youtube' ? video.id : null;

    if (!news && !videoId) {
        return null;
    }

    return (
        <section className="news-short-video">
            <div className="constrain-width large">
                <div className="tile-grid">
                    {news &&
                        <div className="news-short-video-story">
                            {/*
                                The panel is what the rule above and the box
                                around the video are drawn on, inside the gutter
                                the column carries, so both line up with the
                                story and the video rather than with the gap
                                between the two columns.
                            */}
                            <div className="news-short-video-panel">
                                {newsTitle && <h2 className="news-short-video-title">{newsTitle}</h2>}
                                <ArticleTile item={news} />
                            </div>
                        </div>
                    }
                    {videoId &&
                        <div className="tile-grid-sidebar">
                            <ShortVideo id={videoId} title={videoTitle} />
                        </div>
                    }
                </div>
            </div>
        </section>
    );
};

NewsShortVideo.propTypes = {
    data: PropTypes.object.isRequired
};

export default NewsShortVideo;
